import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { workCalendarApi } from "../../services/workCalendarApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { StoreSelect } from "../../components/StoreSelect";
import { useLocalization } from "../../hooks/useLocalization";

export default function WorkCalendarsPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canViewWorkCalendar, canManageWorkCalendar } = usePermissions();
  const [calendars, setCalendars] = useState([]);
  const [form, setForm] = useState({ name: "", scope_type: "ORGANIZATION", store_location: "" });

  const load = useCallback(() => { workCalendarApi.listCalendars().then((r) => setCalendars(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (canViewWorkCalendar) load(); }, [canViewWorkCalendar, load]);

  if (!canViewWorkCalendar) return <p className="text-sm text-gray-500" data-testid="work-calendars-access-denied">{t("common.accessDenied")}</p>;


  const create = async () => {
    if (!form.name) { toast.error(t("mgmtWorkCalendar.nameRequired")); return; }
    if (form.scope_type === "STORE" && !form.store_location) { toast.error(t("mgmtWorkCalendar.selectStore")); return; }
    try {
      await workCalendarApi.createCalendar({ ...form, store_location: form.scope_type === "STORE" ? form.store_location : null });
      toast.success(t("mgmtWorkCalendar.calendarCreated"));
      setForm({ name: "", scope_type: "ORGANIZATION", store_location: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="work-calendars-page">
      <PageHelpBanner pageKey="work-calendars" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtWorkCalendar.help")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtWorkCalendar.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtWorkCalendar.subtitle")}</p>
      </div>

      {canManageWorkCalendar && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="create-calendar-panel">
          <input data-testid="calendar-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("mgmtWorkCalendar.calendarNamePlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <select data-testid="calendar-scope-select" value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
            <option value="ORGANIZATION">{t("mgmtWorkCalendar.organizationWide")}</option>
            <option value="STORE">{t("mgmtWorkCalendar.specificStore")}</option>
          </select>
          {form.scope_type === "STORE" && (
            <StoreSelect testId="calendar-store-input" value={form.store_location} onChange={(v) => setForm({ ...form, store_location: v })} className="px-3 py-1.5" />
          )}
          <button data-testid="create-calendar-btn" onClick={create} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtWorkCalendar.createCalendar")}</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtWorkCalendar.colName")}</th><th className="px-4 py-3 font-medium">{t("mgmtWorkCalendar.colScope")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {calendars.map((c) => (
              <tr key={c.id} className="border-t border-gray-100" data-testid={`calendar-row-${c.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{c.name}</td>
                <td className="px-4 py-2 text-gray-600">{c.scope_type}{c.store_location ? ` (${c.store_location})` : ""}</td>
                <td className="px-4 py-2"><button data-testid={`calendar-open-btn-${c.id}`} onClick={() => navigate(`/management/settings/work-calendars/${c.id}`)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtWorkCalendar.openArrow")}</button></td>
              </tr>
            ))}
            {calendars.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500" data-testid="no-calendars-row">{t("mgmtWorkCalendar.noCalendars")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
